import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Input, Checkbox, Form, Alert, Collapse, Tag, Typography, Spin, AutoComplete, message } from 'antd';
import { PartitionOutlined } from '@ant-design/icons';
import _ from 'lodash';
import apiCore from '../../utils/api-core';
import session from '../../utils/session';
import config from '../../config';
import mixpanel from '../../utils/mixpanel';
import { orderModules } from '../../utils/module';
import { findInvalidMergeTokens, formatInvalidTokenMessage } from '../../utils/mailmerge';
import ModuleGroupPreview from '../module-group/ModuleGroupPreview';
import InterviewMessageInput from '../interview-invites/InterviewMessageInput';
import './EditSequenceModal.css';

const { Text } = Typography;

// Build the update-module payload from an existing module, overriding only the
// response. The update-module endpoint requires title + response and overwrites
// every other field, so we must echo back the module's current values to avoid
// wiping reply_type, options, followups, etc.
const buildModulePayload = (mod, response) => ({
    title: mod.title,
    reply_type: mod.reply_type || '',
    response,
    options: mod.options,
    quick_replies: mod.quick_replies,
    error_response: mod.error_response,
    fallback: mod.fallback,
    field: mod.field,
    email_groups: mod.email_groups,
    followups: mod.followups,
    response_append: mod.response_append,
    response_user_modify: !!mod.response_user_modify,
});

const EditSequenceModal = ({ open, onClose, onSuccess, sequence, existingCategories }) => {
    const sequenceId = sequence?.id;

    const userData = session.get('userData');
    const userRole = userData.role;
    const isOwner = sequence?.owner?.id === userData.id
        || userRole === config.userRoles.ghAdmin
        || userRole === config.userRoles.companyAdmin;
    const canEdit = isOwner;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [flowModalOpen, setFlowModalOpen] = useState(false);
    const [fetched, setFetched] = useState(null);   // raw module-group (incl. modules)
    const [orderedModules, setOrderedModules] = useState([]);
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [responses, setResponses] = useState({});  // moduleId -> edited text

    useEffect(() => {
        if (!open || !sequenceId) return undefined;

        let cancelled = false;
        setLoading(true);
        setFetched(null);
        setFlowModalOpen(false);
        (async () => {
            try {
                const res = await apiCore.get(`/api/v3/module-group/${sequenceId}`);
                const data = res.data || res;
                if (cancelled) return;
                const modules = data.modules || [];
                const ordered = data.module_id ? orderModules(data.module_id, modules) : modules;
                setFetched(data);
                setOrderedModules(ordered);
                setName(data.name || '');
                setCategory(data.category || '');
                setIsPrivate(data.privacy === 'private');
                setResponses(modules.reduce((acc, m) => { acc[m.id] = m.response || ''; return acc; }, {}));
            } catch {
                if (!cancelled) message.error('Failed to load sequence');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [open, sequenceId]);

    const handleResponseChange = (moduleId, value) => {
        setResponses(prev => ({ ...prev, [moduleId]: value }));
    };

    // Validate every message; collect the union of invalid tokens to block save.
    const invalidByModule = useMemo(() => {
        const result = {};
        orderedModules.forEach((mod) => {
            const tokens = findInvalidMergeTokens(responses[mod.id]);
            if (tokens.length) result[mod.id] = tokens;
        });
        return result;
    }, [orderedModules, responses]);

    const hasInvalidTokens = Object.keys(invalidByModule).length > 0;

    const categoryOptions = _.uniqBy((existingCategories || []).map(c => ({ value: c })), 'value');

    const handleSubmit = async () => {
        if (!canEdit) return;
        if (!name.trim()) {
            message.error('Sequence name is required');
            return;
        }
        if (hasInvalidTokens) {
            message.error('Fix the highlighted merge fields before saving');
            return;
        }

        setSubmitting(true);
        try {
            await apiCore.put(`/api/v3/module-group/${sequenceId}`, {
                name: name.trim(),
                category: category || 'Uncategorized',
                privacy: isPrivate ? 'private' : 'public',
            });

            const dirty = orderedModules.filter(mod => (responses[mod.id] || '') !== (mod.response || ''));
            await Promise.all(dirty.map(mod =>
                apiCore.put(`/api/v3/module-group/modules/${mod.id}`, buildModulePayload(mod, responses[mod.id] || ''))
            ));

            mixpanel.track('sequence:update');
            message.success('Sequence updated');
            onSuccess();
            onClose();
        } catch (err) {
            message.error(err.message || 'Failed to update sequence');
        } finally {
            setSubmitting(false);
        }
    };

    const collapseItems = orderedModules.map((mod) => {
        const value = responses[mod.id] !== undefined ? responses[mod.id] : (mod.response || '');
        const tokens = invalidByModule[mod.id] || [];
        return {
            key: mod.id,
            className: tokens.length ? 'has-merge-error' : undefined,
            label: (
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
                    <Tag color={tokens.length ? 'red' : 'green'} style={{ margin: 0, flexShrink: 0 }}>{mod.title}</Tag>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 13,
                            width: '100%',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {value}
                    </Text>
                </span>
            ),
            children: (
                <InterviewMessageInput
                    value={value}
                    onChange={(e) => handleResponseChange(mod.id, e.target.value)}
                    rows={3}
                    disabled={!canEdit}
                    placeholder="Edit message..."
                    showAppend={!!mod.response_append}
                    appendContent={mod.response_append || ''}
                    errorMessage={formatInvalidTokenMessage(tokens)}
                />
            ),
        };
    });

    const footer = [
        <Button key="cancel" onClick={onClose} disabled={submitting} style={{ marginRight: 8 }}>Cancel</Button>,
    ];
    if (canEdit) {
        footer.push(
            <Button key="submit" type="primary" onClick={handleSubmit} loading={submitting} disabled={loading}>
                Save
            </Button>
        );
    }

    return (
        <Modal
            title={<span style={{ fontWeight: 600, fontSize: 18 }}>Edit Sequence</span>}
            open={open}
            onCancel={onClose}
            footer={footer}
            width={840}
            maskClosable={!submitting}
            destroyOnClose
        >
            {loading || !fetched ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                    <Spin tip="Loading sequence..." />
                </div>
            ) : (
                <>
                    {!canEdit && sequence?.owner && (
                        <Alert
                            type="info"
                            showIcon
                            message={`This sequence belongs to ${sequence.owner.name}`}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Button
                        icon={<PartitionOutlined />}
                        onClick={() => setFlowModalOpen(true)}
                        style={{ marginBottom: 16 }}
                    >
                        Preview agent flow
                    </Button>
                    <ModuleGroupPreview
                        moduleGroupId={sequenceId}
                        snapshotData={fetched}
                        flowPreviewMode="modal"
                        flowModalOpen={flowModalOpen}
                        onFlowModalClose={() => setFlowModalOpen(false)}
                    />

                    <Form layout="vertical">
                        <Form.Item label="Sequence Name" style={{ marginBottom: 12 }}>
                            <Input
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. Interview reminder sequence"
                                disabled={!canEdit}
                            />
                        </Form.Item>
                        <Form.Item label="Category" style={{ marginBottom: 12 }}>
                            <AutoComplete
                                value={category}
                                onChange={val => setCategory(val || '')}
                                options={categoryOptions}
                                placeholder="Select or type a category"
                                filterOption={(input, option) => option.value.toUpperCase().indexOf(input.toUpperCase()) !== -1}
                                style={{ width: '100%' }}
                                allowClear
                                disabled={!canEdit}
                            />
                        </Form.Item>
                        {canEdit && (
                            <Form.Item style={{ marginBottom: 12 }}>
                                <Checkbox checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)}>
                                    Make this sequence private
                                </Checkbox>
                            </Form.Item>
                        )}
                    </Form>

                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Messages ({collapseItems.length})
                    </Text>
                    <Collapse size="small" items={collapseItems} bordered={false} className="seq-edit-collapse" />
                </>
            )}
        </Modal>
    );
};

export default EditSequenceModal;
